import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { detectStoredLanguage, initI18n, normalizeLanguage, persistLanguage } from "./config";

// Initialize i18n once at module load so translations are ready before render.
initI18n();

/**
 * Keeps i18n in sync with the signed-in user's `profiles.language` column.
 * Falls back to detected language (localStorage / navigator) when signed out.
 * Also mirrors the current language onto the <html lang> attribute.
 */
export function LanguageSync() {
  const { i18n } = useTranslation();

  // Apply the stored/browser language only after hydration.
  useEffect(() => {
    const detected = detectStoredLanguage();
    if (i18n.language !== detected) void i18n.changeLanguage(detected);
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.language ?? "en";
  }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;

    async function loadForUser() {
      const { data } = await supabase.auth.getUser();
      if (cancelled || !data.user) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("language")
        .eq("id", data.user.id)
        .maybeSingle();
      if (cancelled) return;
      const raw = profile?.language as string | null | undefined;
      if (raw) {
        const normalized = normalizeLanguage(raw);
        persistLanguage(normalized);
        if (i18n.language !== normalized) void i18n.changeLanguage(normalized);
      }
    }

    void loadForUser();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "USER_UPDATED") void loadForUser();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [i18n]);

  return null;
}